import { TERRITORY } from "$routes/_lib/constants";
import { NationalIdOwner, NationalIdType } from "elephant-web/gql";
import { pipe, replace, test } from "ramda";
import * as Yup from "yup";

export const getNationalIdValidationSchema = (territory: string) => {
  switch (territory) {
    case TERRITORY.PAKISTAN:
      return Yup.object({
        number: Yup.string()
          .required()
          .test(
            "CNIC validation",
            "Please enter a valid 13 digit CNIC number",
            (input) =>
              pipe(
                replace(/\s/g, ""),
                replace(/-/g, ""),
                test(/^[0-9]{13}$/),
              )(input ?? ""),
          ),
        type: Yup.mixed<NationalIdType>()
          .oneOf(Object.values(NationalIdType))
          .required(),
        owner: Yup.mixed<NationalIdOwner>().when("type", {
          is: NationalIdType.Secondary,
          then: (schema) =>
            schema.oneOf(Object.values(NationalIdOwner)).required(),
          otherwise: (schema) => schema.optional(),
        }),
      }).required();

    case TERRITORY.KENYA:
      return Yup.object({
        number: Yup.string().test(
          "nationalId",
          "Please enter a valid 7 or 8 digit national ID number",
          (input) =>
            !input || pipe(replace(/[-\s]/g, ""), test(/^[0-9]{7,8}$/))(input),
        ),
        type: Yup.mixed<NationalIdType>().optional(),
        owner: Yup.mixed<NationalIdOwner>().optional(),
      });

    case TERRITORY.NIGERIA:
      return Yup.object({
        number: Yup.string().test(
          "nationalId",
          "Please enter a valid 11 digit NIN",
          (input) =>
            !input || pipe(replace(/[-\s]/g, ""), test(/^[0-9]{11}$/))(input),
        ),
        type: Yup.mixed<NationalIdType>().optional(),
        owner: Yup.mixed<NationalIdOwner>().optional(),
      });

    case TERRITORY.LIBERIA:
      return Yup.object({
        number: Yup.string().test(
          "nationalId",
          "Please enter a valid 10 digit National ID number",
          (input) => !input || /^[0-9]{10}$/.test(input),
        ),
        type: Yup.mixed<NationalIdType>().optional(),
        owner: Yup.mixed<NationalIdOwner>().optional(),
      });

    default:
      return Yup.object({
        number: Yup.string(),
        type: Yup.mixed<NationalIdType>().optional(),
        owner: Yup.mixed<NationalIdOwner>().optional(),
      });
  }
};

type NationalId = Yup.InferType<
  ReturnType<typeof getNationalIdValidationSchema>
>;

export const getNationalIdNumber = (
  nationalId: NationalId | null,
): string | null => {
  return nationalId?.number || null;
};

export const getNationalIdOwner = (
  nationalId: NationalId | null,
): NationalIdOwner => {
  if (!nationalId) return NationalIdOwner.Self;

  // Pakistan specific logic
  if (nationalId.type && nationalId.owner) {
    return nationalId.type === NationalIdType.Primary
      ? NationalIdOwner.Self
      : nationalId.owner;
  }

  // Default for other territories
  return NationalIdOwner.Self;
};
